import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Feather, ScrollText, GraduationCap } from "lucide-react";
import { SEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tracks = [
  { id: "curated", name: "Curated House List", icon: ScrollText, desc: "Rigorous editorial review. If accepted, CBH publishes, prints, distributes, and markets at scale." },
  { id: "outpublish", name: "Conlang Outpublish", icon: Feather, desc: "Author-led publishing with CBH's editorial, design, and distribution muscle behind you." },
  { id: "fellows", name: "Conlang Fellows", icon: GraduationCap, desc: "For scholars, civil servants, and public intellectuals working on long-form monographs." },
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  country: "",
  working_title: "",
  genre: "",
  track: "curated",
  word_count: "",
  synopsis: "",
  author_bio: "",
};

export default function PublishWithUs() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, word_count: form.word_count ? Number(form.word_count) : null };
      const { data } = await axios.post(`${API}/manuscripts`, payload);
      setSubmitted(data);
      setForm(initial);
      toast.success("Manuscript received", { description: `Reference: ${data.submission_number}` });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="publish-page">
      <SEO
        title="Publish With Us"
        path="/publish"
        description="Submit your manuscript to Conlang Book House. Three tracks — Curated House List, Conlang Outpublish, and Conlang Fellows. Response within 4–6 weeks."
        keywords="publish book India, manuscript submission, Indian publisher submission, CBH Outpublish"
      />
      <Toaster position="top-center" richColors />
      {/* Hero */}
      <section className="pt-16 lg:pt-24 pb-16 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Publish With Us</p>
            <h1 className="font-editorial text-6xl md:text-8xl leading-[0.9] tracking-tighter text-ink text-balance">
              You have a book <em className="text-crimson not-italic">in you.</em>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ink-muted">
              CBH publishes across three tracks and one very high bar. Whatever the shape of
              your manuscript, this is where it starts.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1473186505569-9c61870c11f9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
              alt="Fountain pen on manuscript"
              className="w-full aspect-[4/5] object-cover book-cover-3d"
            />
          </div>
        </div>
      </section>

      {/* Tracks */}
      <section className="bg-ivory-soft py-24" data-testid="publish-tracks">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Three Tracks</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-16 text-balance">
            Choose your <em className="text-crimson not-italic">path.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-crimson/20">
            {tracks.map(({ id, name, icon: Icon, desc }) => (
              <button
                type="button"
                key={id}
                onClick={() => update("track", id)}
                data-testid={`publish-track-${id}`}
                className={`text-left p-10 transition-colors duration-500 ${form.track === id ? "bg-crimson text-ivory" : "bg-ivory text-ink hover:bg-crimson/5"}`}
              >
                <Icon strokeWidth={1} className={`w-10 h-10 mb-6 ${form.track === id ? "text-gold-light" : "text-crimson"}`} />
                <h3 className="font-editorial text-2xl mb-3">{name}</h3>
                <p className={`text-sm leading-relaxed ${form.track === id ? "text-ivory/80" : "text-ink-muted"}`}>{desc}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-24" data-testid="publish-form-section">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Submit Manuscript</p>
          <h2 className="font-editorial text-5xl text-ink leading-tight mb-4">Send it in.</h2>
          <p className="text-ink-muted mb-12">
            Our editorial board reviews submissions on a rolling basis. Expect a response
            within four to six weeks.
          </p>

          {submitted ? (
            <div className="border border-crimson/30 bg-ivory-soft p-10" data-testid="publish-submitted-panel">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">Received</p>
              <h3 className="font-editorial text-3xl mb-4">Thank you.</h3>
              <p className="text-ink-muted mb-6">
                Your manuscript has been logged with reference{" "}
                <span className="font-editorial text-crimson">{submitted.submission_number}</span>. The
                editorial board will respond by email.
              </p>
              <button
                onClick={() => setSubmitted(null)}
                className="text-sm uppercase tracking-[0.24em] text-crimson link-underline"
                data-testid="publish-submit-another"
              >
                Submit another →
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="publish-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Full name" required>
                  <input required data-testid="publish-full-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Email" required>
                  <input required type="email" data-testid="publish-email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Phone">
                  <input data-testid="publish-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Country">
                  <input data-testid="publish-country" value={form.country} onChange={(e) => update("country", e.target.value)} className={inputCls} />
                </Field>
              </div>
              <Field label="Working title" required>
                <input required data-testid="publish-working-title" value={form.working_title} onChange={(e) => update("working_title", e.target.value)} className={inputCls} />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Genre / subject" required>
                  <input required data-testid="publish-genre" value={form.genre} onChange={(e) => update("genre", e.target.value)} className={inputCls} placeholder="e.g. Political biography, Historical fiction" />
                </Field>
                <Field label="Approx. word count">
                  <input type="number" data-testid="publish-word-count" value={form.word_count} onChange={(e) => update("word_count", e.target.value)} className={inputCls} />
                </Field>
              </div>
              <Field label="Publishing track" required>
                <select required value={form.track} onChange={(e) => update("track", e.target.value)} data-testid="publish-track-select" className={inputCls}>
                  {tracks.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Synopsis (500–1500 words recommended)" required>
                <textarea required rows={8} data-testid="publish-synopsis" value={form.synopsis} onChange={(e) => update("synopsis", e.target.value)} className={inputCls} minLength={40} />
              </Field>
              <Field label="Author bio">
                <textarea rows={4} data-testid="publish-author-bio" value={form.author_bio} onChange={(e) => update("author_bio", e.target.value)} className={inputCls} />
              </Field>
              <button
                type="submit"
                disabled={submitting}
                data-testid="publish-submit-btn"
                className="w-full bg-crimson text-ivory px-8 py-5 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500 disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Send Manuscript →"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-ivory border border-crimson/20 px-4 py-3 text-sm focus:outline-none focus:border-crimson transition-colors duration-300";
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.22em] text-ink-muted mb-2">
      {label} {required && <span className="text-crimson">*</span>}
    </span>
    {children}
  </label>
);
